<template>
  <div class="painel-wrap">
    <!-- Login -->
    <div v-if="!autenticado" class="login-card">
      <h2>Painel Admin</h2>
      <input
        v-model="tokenInput"
        type="password"
        placeholder="Token de acesso"
        @keyup.enter="entrar"
      />
      <button @click="entrar" :disabled="carregando">Entrar</button>
      <p v-if="erroLogin" class="erro">{{ erroLogin }}</p>
    </div>

    <!-- Painel principal -->
    <div v-else class="painel">
      <header class="painel-header">
        <span>Painel Admin</span>
        <div class="header-actions">
          <span class="visit-counter">Visitas: {{ formatNumber(metricas.visitas) }}</span>
          <button class="btn-sm btn-cancel" @click="limparVisitas" :disabled="carregandoAdmin">Limpar visitas</button>
          <button class="btn-sair" @click="sair">Sair</button>
        </div>
      </header>

      <div class="painel-shell">
        <aside class="painel-nav">
          <button
            v-for="item in navItems"
            :key="item.id"
            :class="abaAtiva === item.id ? 'nav-item nav-item-ativa' : 'nav-item'"
            @click="abaAtiva = item.id"
          >
            <span>{{ item.label }}</span>
            <strong>{{ item.count !== null ? formatNumber(item.count) : '' }}</strong>
          </button>
        </aside>

        <main class="painel-body">
          <section v-if="abaAtiva === 'resumo'" class="card dash">
            <div class="card-header dash-head">
              <div>
                <h3>Dashboard</h3>
                <p class="auto-refresh-hint auto-refresh-hint-left">
                  <span class="live-dot"></span>
                  <template v-if="ultimaAtualizacao">Atualiza sozinho a cada 15s · última {{ formatDateTime(ultimaAtualizacao) }}</template>
                  <template v-else>Carregando dados…</template>
                </p>
              </div>
              <div class="dash-actions">
                <button class="btn-sm" @click="carregarDashboard" :disabled="carregandoAdmin">
                  {{ carregandoAdmin ? 'Atualizando…' : 'Atualizar' }}
                </button>
                <button class="btn-sm btn-cancel" @click="zerarContadores" :disabled="carregandoAdmin">
                  Zerar contadores
                </button>
              </div>
            </div>

            <p v-if="erroAdmin" class="erro">{{ erroAdmin }}</p>

            <div class="stat-grid">
              <div class="stat-card kpi kpi-blue">
                <span class="kpi-label">Visitas</span>
                <strong>{{ formatNumber(metricas.visitas) }}</strong>
                <span class="kpi-foot">pageviews registrados</span>
              </div>
              <div class="stat-card kpi kpi-violet">
                <span class="kpi-label">PIX gerados</span>
                <strong>{{ formatNumber(metricas.pix_gerados) }}</strong>
                <span class="kpi-foot">códigos emitidos</span>
              </div>
              <div class="stat-card kpi kpi-mint">
                <span class="kpi-label">PIX copiados</span>
                <strong>{{ formatNumber(metricas.pix_copiados) }}</strong>
                <span class="kpi-foot">clientes que copiaram</span>
              </div>
              <div class="stat-card kpi kpi-amber">
                <span class="kpi-label">Acessos</span>
                <strong>{{ formatNumber(metricas.acessos) }}</strong>
                <span class="kpi-foot">eventos registrados</span>
              </div>
            </div>

            <div class="stat-grid stat-grid-3">
              <div class="stat-card kpi kpi-ghost">
                <span class="kpi-label">Taxa de cópia</span>
                <strong>{{ taxaCopia === null ? '—' : taxaCopia + '%' }}</strong>
                <span class="kpi-foot">copiados ÷ gerados</span>
              </div>
              <div class="stat-card kpi kpi-ghost">
                <span class="kpi-label">Ticket médio</span>
                <strong class="kpi-money">{{ ticketMedio === null ? '—' : formatCurrency(ticketMedio) }}</strong>
                <span class="kpi-foot">média dos PIX gerados</span>
              </div>
              <div class="stat-card kpi kpi-ghost">
                <span class="kpi-label">PIX pendentes</span>
                <strong>{{ formatNumber(pixPendentes) }}</strong>
                <span class="kpi-foot">gerados e não copiados</span>
              </div>
            </div>

            <div class="dash-chart">
              <div class="dash-panel-head">
                <h4>PIX gerados por dia</h4>
                <span class="dash-note">últimos 7 dias</span>
              </div>
              <div class="chart-bars">
                <div v-for="d in pixPorDia" :key="d.key" class="chart-col">
                  <span class="chart-value">{{ d.value }}</span>
                  <div class="chart-track">
                    <div class="chart-fill" :style="{ height: d.pct + '%' }"></div>
                  </div>
                  <span class="chart-label">{{ d.label }}</span>
                </div>
              </div>
            </div>

            <div class="dash-cols">
              <div class="dash-panel">
                <div class="dash-panel-head">
                  <h4>Últimos acessos</h4>
                  <button class="btn-link" @click="abaAtiva = 'acessos'">ver todos</button>
                </div>
                <ul class="mini-list">
                  <li v-for="a in acessosRecentes" :key="a.id">
                    <span class="mini-dot"></span>
                    <div class="mini-main">
                      <strong>{{ accessLabel(a.type) }}</strong>
                      <span class="mini-path">{{ a.path }}</span>
                    </div>
                    <div class="mini-side">
                      <span>{{ a.localizacao || a.ip || '-' }}</span>
                      <small>{{ formatDateTime(a.created_at) }}</small>
                    </div>
                  </li>
                  <li v-if="!acessosRecentes.length" class="mini-empty">Nenhum acesso ainda.</li>
                </ul>
              </div>

              <div class="dash-panel">
                <div class="dash-panel-head">
                  <h4>Últimos PIX</h4>
                  <button class="btn-link" @click="abaAtiva = 'pix-gerados'">ver todos</button>
                </div>
                <ul class="mini-list">
                  <li v-for="p in pixRecentes" :key="p.key">
                    <span class="mini-dot" :class="p.copiado_em ? 'mini-dot-ok' : 'mini-dot-warn'"></span>
                    <div class="mini-main">
                      <strong>{{ formatCurrency(p.valor) }}</strong>
                      <span class="mini-path">{{ p.referencia || p.key }}</span>
                    </div>
                    <div class="mini-side">
                      <span>{{ p.copiado_em ? 'copiado' : 'pendente' }}</span>
                      <small>{{ formatDateTime(p.gerado_em) }}</small>
                    </div>
                  </li>
                  <li v-if="!pixRecentes.length" class="mini-empty">Nenhum PIX gerado ainda.</li>
                </ul>
              </div>
            </div>
          </section>

          <section v-if="abaAtiva === 'projetos'" class="card">
            <div class="card-header">
              <h3>Projetos</h3>
              <button class="btn-sm" @click="abrirCriar">+ Novo</button>
            </div>

            <div v-if="criandoProjeto" class="form-novo">
              <input v-model="novoProjeto.name" placeholder="Nome interno (slug)" />
              <input v-model="novoProjeto.original_name" placeholder="Nome de exibição" />
              <div class="form-novo-btns">
                <button class="btn-sm" @click="criarProjeto" :disabled="carregando">Criar</button>
                <button class="btn-sm btn-cancel" @click="criandoProjeto = false">Cancelar</button>
              </div>
            </div>

            <p v-if="erroProj" class="erro">{{ erroProj }}</p>

            <div v-if="carregandoProj" class="loading">Carregando…</div>
            <table v-else-if="projetos.length" class="tabela">
              <thead>
                <tr><th>Nome</th><th>Exibição</th><th>Dist</th><th>URL</th><th></th></tr>
              </thead>
              <tbody>
                <tr v-for="p in projetos" :key="p.name">
                  <td>{{ p.name }}</td>
                  <td>{{ p.original_name }}</td>
                  <td>
                    <span :class="p.hasDist ? 'badge-ok' : 'badge-no'">
                      {{ p.hasDist ? 'sim' : 'não' }}
                    </span>
                  </td>
                  <td>
                    <a v-if="p.url" :href="p.url" target="_blank" class="link">abrir</a>
                  </td>
                  <td>
                    <button class="btn-del" @click="deletarProjeto(p.name)" title="Deletar">✕</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-else class="vazio">Nenhum projeto encontrado.</p>
          </section>

          <section v-if="abaAtiva === 'pix-gerados'" class="card">
            <div class="card-header">
              <h3>PIX gerados</h3>
              <button class="btn-sm" @click="carregarDashboard" :disabled="carregandoAdmin">Atualizar</button>
            </div>
            <div v-if="carregandoAdmin && !pixGerados.length" class="loading">Carregando…</div>
            <table v-else-if="pixGerados.length" class="tabela">
              <thead>
                <tr><th>Chave</th><th>Valor</th><th>Referência</th><th>Gerado em</th><th>Status</th><th>Link</th></tr>
              </thead>
              <tbody>
                <template v-for="g in pixGeradosPorDia" :key="g.key">
                  <tr class="grupo-data">
                    <td colspan="6">{{ g.label }}<span class="grupo-count">{{ g.itens.length }}</span></td>
                  </tr>
                  <tr v-for="item in g.itens" :key="item.key">
                    <td>{{ item.key }}</td>
                    <td>{{ formatCurrency(item.valor) }}</td>
                    <td>{{ item.referencia || '-' }}</td>
                    <td>{{ formatDateTime(item.gerado_em) }}</td>
                    <td><span :class="item.copiado_em ? 'badge-ok' : 'badge-warn'">{{ item.copiado_em ? 'copiado' : 'pendente' }}</span></td>
                    <td><a :href="item.checkout_url" target="_blank" class="link">abrir</a></td>
                  </tr>
                </template>
              </tbody>
            </table>
            <p v-else class="vazio">Nenhum PIX gerado encontrado.</p>
          </section>

          <section v-if="abaAtiva === 'pix-copiados'" class="card">
            <div class="card-header">
              <h3>PIX copiados</h3>
              <button class="btn-sm" @click="carregarDashboard" :disabled="carregandoAdmin">Atualizar</button>
            </div>
            <div v-if="carregandoAdmin && !pixCopiados.length" class="loading">Carregando…</div>
            <table v-else-if="pixCopiados.length" class="tabela">
              <thead>
                <tr><th>Chave</th><th>Valor</th><th>PIX</th><th>Copiado em</th><th>Referência</th></tr>
              </thead>
              <tbody>
                <template v-for="g in pixCopiadosPorDia" :key="g.key">
                  <tr class="grupo-data">
                    <td colspan="5">{{ g.label }}<span class="grupo-count">{{ g.itens.length }}</span></td>
                  </tr>
                  <tr v-for="item in g.itens" :key="item.key">
                    <td>{{ item.key }}</td>
                    <td>{{ formatCurrency(item.valor) }}</td>
                    <td class="mono">{{ item.pix_preview }}</td>
                    <td>{{ formatDateTime(item.copiado_em) }}</td>
                    <td>{{ item.referencia || '-' }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
            <p v-else class="vazio">Nenhum PIX copiado encontrado.</p>
          </section>

          <section v-if="abaAtiva === 'acessos'" class="card">
            <div class="card-header">
              <h3>Acessos</h3>
              <button class="btn-sm" @click="carregarDashboard" :disabled="carregandoAdmin">Atualizar</button>
            </div>
            <div v-if="carregandoAdmin && !acessos.length" class="loading">Carregando…</div>
            <table v-else-if="acessos.length" class="tabela">
              <thead>
                <tr><th>Data</th><th>Tipo</th><th>Rota</th><th>IP</th><th>Localização</th><th>Chave</th></tr>
              </thead>
              <tbody>
                <tr v-for="item in acessos" :key="item.id">
                  <td>{{ formatDateTime(item.created_at) }}</td>
                  <td><span class="badge-soft">{{ accessLabel(item.type) }}</span></td>
                  <td class="path-cell">{{ item.path }}</td>
                  <td>{{ item.ip || '-' }}</td>
                  <td>{{ item.localizacao || '-' }}</td>
                  <td>{{ item.key || '-' }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="vazio">Nenhum acesso registrado.</p>
          </section>

          <section v-if="abaAtiva === 'historico'" class="card">
            <div class="card-header">
              <h3>Histórico</h3>
              <button class="btn-sm" @click="carregarDashboard" :disabled="carregandoAdmin">Atualizar</button>
            </div>
            <div v-if="carregandoAdmin && !historico.length" class="loading">Carregando…</div>
            <table v-else-if="historico.length" class="tabela">
              <thead>
                <tr><th>PIX gerado</th><th>PIX copiado</th><th>Dados de quem gerou</th><th>Valor</th><th>Chave</th></tr>
              </thead>
              <tbody>
                <template v-for="g in historicoPorDia" :key="g.key">
                  <tr class="grupo-data">
                    <td colspan="5">{{ g.label }}<span class="grupo-count">{{ g.itens.length }}</span></td>
                  </tr>
                  <tr v-for="item in g.itens" :key="item.key">
                    <td>
                      <span class="badge-warn">{{ formatDateTime(item.pix_gerado) }}</span>
                      <div class="mono">{{ item.pix_preview || '-' }}</div>
                    </td>
                    <td>
                      <span :class="item.pix_copiado ? 'badge-ok' : 'badge-no'">
                        {{ item.pix_copiado ? formatDateTime(item.pix_copiado) : 'não copiado' }}
                      </span>
                    </td>
                    <td class="generator-cell">
                      <div v-for="linha in generatorDataLines(item)" :key="linha.label">
                        <strong>{{ linha.label }}:</strong> {{ linha.value }}
                      </div>
                    </td>
                    <td>{{ item.valor ? formatCurrency(item.valor) : '-' }}</td>
                    <td>{{ item.key || '-' }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
            <p v-else class="vazio">Nenhum histórico encontrado.</p>
          </section>

          <!-- ════════════════════ Aba Gateway PIX ════════════════════════ -->
          <section v-if="abaAtiva === 'gateway'" class="card">
            <div class="card-header">
              <h3>Gateway PIX</h3>
              <button class="btn-sm" @click="carregarGateway" :disabled="carregandoGateway">
                {{ carregandoGateway ? 'Carregando…' : 'Atualizar' }}
              </button>
            </div>

            <p v-if="erroGateway" class="erro">{{ erroGateway }}</p>

            <div class="gateway-wrap">
              <p class="gateway-descricao">
                Selecione qual processador será usado para gerar cobranças PIX. A mudança tem efeito imediato para novas cobranças.
              </p>

              <div class="gateway-options">
                <label
                  v-for="gw in gatewaysDisponiveis"
                  :key="gw.id"
                  :class="['gateway-card', gatewayAtivo === gw.id ? 'gateway-card-ativo' : '']"
                >
                  <input
                    type="radio"
                    :value="gw.id"
                    v-model="gatewayEscolhido"
                    class="gateway-radio"
                  />
                  <div class="gateway-info">
                    <strong>{{ gw.nome }}</strong>
                    <span>{{ gw.descricao }}</span>
                  </div>
                  <span v-if="gatewayAtivo === gw.id" class="badge-ok">ativo</span>
                </label>
              </div>

              <div class="gateway-actions">
                <button
                  class="btn-sm"
                  :disabled="carregandoGateway || gatewayEscolhido === gatewayAtivo"
                  @click="salvarGateway"
                >
                  {{ carregandoGateway ? 'Salvando…' : 'Salvar gateway' }}
                </button>
                <span v-if="gatewayEscolhido === gatewayAtivo" class="gateway-hint">Nenhuma alteração pendente.</span>
              </div>

              <!-- Chave PIX (recebimento direto, sem gateway) -->
              <div class="cred-section">
                <h4 class="cred-title">Chave PIX</h4>
                <p class="gateway-descricao" style="margin-bottom:14px">
                  Usada quando o gateway selecionado for <strong>Chave PIX</strong>. A cobrança é gerada localmente
                  (BR Code) com o valor da fatura — sem passar por adquirente.
                </p>

                <p v-if="erroChavePix" class="erro">{{ erroChavePix }}</p>

                <div v-if="chavePix && !editandoChavePix" class="pixkey-atual">
                  <div class="pixkey-row">
                    <span class="pixkey-label">Chave cadastrada</span>
                    <span class="pixkey-value mono">{{ chavePix.chave }}</span>
                  </div>
                  <div class="pixkey-row">
                    <span class="pixkey-label">Tipo</span>
                    <span class="pixkey-value">{{ chavePix.tipo_label || chavePix.tipo }}</span>
                  </div>
                  <div class="pixkey-row">
                    <span class="pixkey-label">Recebedor</span>
                    <span class="pixkey-value">{{ chavePix.nome }} — {{ chavePix.cidade }}</span>
                  </div>
                  <div class="gateway-actions" style="margin-top:14px">
                    <button class="btn-sm" @click="iniciarEdicaoChave" :disabled="carregandoChave">Alterar</button>
                    <button class="btn-sm btn-cancel" @click="removerChavePix" :disabled="carregandoChave">Remover</button>
                  </div>
                </div>

                <div v-else>
                  <div class="cred-field">
                    <label class="cred-label">Chave PIX</label>
                    <input
                      v-model="chavePixInput"
                      class="cred-input"
                      placeholder="CPF, CNPJ, e-mail, telefone com DDD ou aleatória"
                      autocomplete="off"
                    />
                  </div>
                  <div class="cred-field">
                    <label class="cred-label">Nome do recebedor</label>
                    <input
                      v-model="chavePixNome"
                      class="cred-input"
                      placeholder="EQUATORIAL ENERGIA"
                      autocomplete="off"
                    />
                  </div>
                  <div class="cred-field">
                    <label class="cred-label">Cidade</label>
                    <input
                      v-model="chavePixCidade"
                      class="cred-input"
                      placeholder="SAO PAULO"
                      autocomplete="off"
                    />
                  </div>
                  <div class="gateway-actions">
                    <button class="btn-sm" :disabled="carregandoChave" @click="salvarChavePix">
                      {{ carregandoChave ? 'Salvando…' : (chavePix ? 'Salvar alteração' : 'Cadastrar chave') }}
                    </button>
                    <button v-if="chavePix" class="btn-sm btn-cancel" @click="cancelarEdicaoChave">
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>

              <!-- Fatura fallback -->
              <div class="cred-section">
                <h4 class="cred-title">Fatura fallback</h4>
                <p class="gateway-descricao" style="margin-bottom:14px">
                  Quando a consulta na Equatorial falha, o cliente recebe uma fatura do mês anterior com valor
                  aleatório dentro desta faixa. "Usar padrão" devolve os valores originais.
                </p>

                <p v-if="erroFallback" class="erro">{{ erroFallback }}</p>

                <div class="fallback-grid">
                  <div class="cred-field">
                    <label class="cred-label">Valor mínimo (R$)</label>
                    <input
                      v-model.number="fallbackConfig.valor_min"
                      class="cred-input"
                      type="number"
                      min="1"
                      step="0.01"
                      inputmode="decimal"
                    />
                  </div>
                  <div class="cred-field">
                    <label class="cred-label">Valor máximo (R$)</label>
                    <input
                      v-model.number="fallbackConfig.valor_max"
                      class="cred-input"
                      type="number"
                      min="1"
                      step="0.01"
                      inputmode="decimal"
                    />
                  </div>
                </div>

                <p class="gateway-hint">
                  Padrão: {{ formatCurrency(fallbackPadrao.valor_min) }} a {{ formatCurrency(fallbackPadrao.valor_max) }}
                  · contrato {{ fallbackConfig.contrato || fallbackPadrao.contrato }}
                </p>

                <div class="gateway-actions">
                  <button class="btn-sm" :disabled="carregandoFallback" @click="salvarFallback">
                    {{ carregandoFallback ? 'Salvando…' : 'Salvar valores' }}
                  </button>
                  <button class="btn-sm btn-cancel" :disabled="carregandoFallback" @click="restaurarFallbackPadrao">
                    Usar padrão
                  </button>
                </div>
              </div>

              <!-- Credenciais -->
              <div class="cred-section">
                <h4 class="cred-title">Credenciais</h4>
                <p class="gateway-descricao" style="margin-bottom:14px">
                  Deixe em branco para usar o valor padrão configurado no servidor. Ao salvar, a nova chave entra em vigor imediatamente para novas cobranças.
                </p>

                <div class="cred-field">
                  <label class="cred-label">
                    SuperPay BR — Authorization
                    <span v-if="credSuperpayHint" class="cred-hint">atual: {{ credSuperpayHint }}</span>
                  </label>
                  <div class="cred-input-row">
                    <input
                      v-model="credSuperpayInput"
                      :type="credSuperpayVisivel ? 'text' : 'password'"
                      placeholder="Basic c2t…  (deixe vazio para usar o padrão)"
                      class="cred-input"
                      autocomplete="off"
                    />
                    <button class="btn-sm btn-cancel" @click="credSuperpayVisivel = !credSuperpayVisivel">
                      {{ credSuperpayVisivel ? 'Ocultar' : 'Mostrar' }}
                    </button>
                  </div>
                </div>

                <div class="cred-field">
                  <label class="cred-label">
                    SigmaPayments — X-API-Key
                    <span v-if="credSigmaHint" class="cred-hint">atual: {{ credSigmaHint }}</span>
                  </label>
                  <div class="cred-input-row">
                    <input
                      v-model="credSigmaInput"
                      :type="credSigmaVisivel ? 'text' : 'password'"
                      placeholder="pk_live_…  (deixe vazio para usar o padrão)"
                      class="cred-input"
                      autocomplete="off"
                    />
                    <button class="btn-sm btn-cancel" @click="credSigmaVisivel = !credSigmaVisivel">
                      {{ credSigmaVisivel ? 'Ocultar' : 'Mostrar' }}
                    </button>
                  </div>
                </div>

                <p v-if="erroCredenciais" class="erro">{{ erroCredenciais }}</p>

                <div class="gateway-actions">
                  <button class="btn-sm" :disabled="carregandoCredenciais" @click="salvarCredenciais">
                    {{ carregandoCredenciais ? 'Salvando…' : 'Salvar credenciais' }}
                  </button>
                  <button class="btn-sm btn-cancel" @click="carregarCredenciais" :disabled="carregandoCredenciais">Recarregar</button>
                </div>
              </div>
            </div>
          </section>

          <section v-if="abaAtiva === 'pm2'" class="card">
            <div class="card-header">
              <h3>Serviços PM2</h3>
              <button class="btn-sm" @click="carregarPm2">Atualizar</button>
            </div>

            <!-- Painel de ações de infraestrutura: ações destrutivas que afetam TUDO no servidor.
                 Visualmente separadas da tabela e exigem confirmação SweetAlert. -->
            <div class="infra-actions">
              <div class="infra-action">
                <div class="infra-text">
                  <strong>PM2 — restart all</strong>
                  <span>Reinicia todos os serviços Node gerenciados (downtime ~1-2s).</span>
                </div>
                <button
                  class="btn-sm btn-warn"
                  :disabled="infraBusy === 'pm2'"
                  @click="restartPm2All"
                >
                  {{ infraBusy === 'pm2' ? 'Reiniciando…' : 'pm2 restart all' }}
                </button>
              </div>
              <div class="infra-action">
                <div class="infra-text">
                  <strong>Nginx — restart completo</strong>
                  <span>Valida config, limpa <code>/var/cache/nginx</code> + <code>/var/lib/nginx</code> e reinicia o serviço.</span>
                </div>
                <button
                  class="btn-sm btn-warn"
                  :disabled="infraBusy === 'nginx'"
                  @click="restartNginx"
                >
                  {{ infraBusy === 'nginx' ? 'Reiniciando…' : 'nginx restart' }}
                </button>
              </div>
            </div>

            <p v-if="erroPm2" class="erro">{{ erroPm2 }}</p>

            <div v-if="carregandoPm2 && !pm2.length" class="loading">Carregando…</div>
            <table v-else-if="pm2.length" class="tabela">
              <thead>
                <tr><th>Nome</th><th>Status</th><th>Restarts</th><th>Ações</th></tr>
              </thead>
              <tbody>
                <tr v-for="s in pm2" :key="s.name">
                  <td>{{ s.name }}</td>
                  <td>
                    <span :class="statusClass(s.status)">{{ s.status }}</span>
                  </td>
                  <td>{{ s.restarts }}</td>
                  <td class="acoes">
                    <button class="btn-sm" @click="pm2Action(s.name, 'restart')">Restart</button>
                    <button class="btn-sm btn-warn" @click="pm2Action(s.name, 'stop')">Stop</button>
                    <button class="btn-sm" @click="verLog(s.name)">Log</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-else class="vazio">Nenhum serviço PM2.</p>

            <div v-if="logSvc" class="log-box">
              <div class="log-header">
                <span>Log: {{ logSvc }}</span>
                <button class="btn-sm btn-cancel" @click="logSvc = null; logTexto = ''">✕</button>
              </div>
              <div class="log-tabs">
                <button :class="logAba === 'out' ? 'aba-ativa' : 'aba'" @click="logAba = 'out'">stdout</button>
                <button :class="logAba === 'err' ? 'aba-ativa' : 'aba'" @click="logAba = 'err'">stderr</button>
              </div>
              <pre class="log-pre">{{ logAba === 'out' ? logOut : logErr }}</pre>
            </div>
          </section>

          <!-- ════════════════════ Aba Pool OTP (warm pool) ═════════════════ -->
          <section v-if="abaAtiva === 'warm'" class="card">
            <div class="card-header">
              <h3>Pool OTP (browser contexts pré-aquecidos)</h3>
              <button class="btn-sm" @click="carregarWarm" :disabled="carregandoWarm">
                {{ carregandoWarm ? 'Carregando…' : 'Atualizar' }}
              </button>
            </div>

            <p v-if="erroWarm" class="erro">{{ erroWarm }}</p>

            <p v-if="!warmOtp" class="vazio">
              Sem dados ainda — clique em <strong>Atualizar</strong>.
            </p>

            <div v-else>
              <!-- Resumo agregado -->
              <div class="warm-summary">
                <div class="warm-metric">
                  <span class="warm-metric-num">{{ warmOtp.built || 0 }}</span>
                  <span class="warm-metric-label">Construídos</span>
                </div>
                <div class="warm-metric">
                  <span class="warm-metric-num">{{ warmOtp.served || 0 }}</span>
                  <span class="warm-metric-label">Servidos</span>
                </div>
                <div class="warm-metric warm-metric-ok">
                  <span class="warm-metric-num">{{ warmOtp.hit || 0 }}</span>
                  <span class="warm-metric-label">Hits</span>
                </div>
                <div class="warm-metric warm-metric-warn">
                  <span class="warm-metric-num">{{ warmOtp.miss || 0 }}</span>
                  <span class="warm-metric-label">Miss</span>
                </div>
                <div class="warm-metric">
                  <span class="warm-metric-num">{{ warmOtp.expired || 0 }}</span>
                  <span class="warm-metric-label">Expirados</span>
                </div>
                <div class="warm-metric warm-metric-bad">
                  <span class="warm-metric-num">{{ warmOtp.errors || 0 }}</span>
                  <span class="warm-metric-label">Erros</span>
                </div>
                <div class="warm-metric warm-metric-rate">
                  <span class="warm-metric-num">{{ warmHitRate === null ? '–' : warmHitRate + '%' }}</span>
                  <span class="warm-metric-label">Hit rate</span>
                </div>
              </div>

              <p class="warm-ttl">
                TTL por ctx: <strong>{{ Math.round((warmOtp.ttlMs || 0) / 1000) }}s</strong> (limite Imperva).
                Disponíveis agora: <strong>{{ warmTotalAvailable }}</strong>.
              </p>

              <!-- Buckets por estado -->
              <table class="tabela warm-tabela">
                <thead>
                  <tr>
                    <th>Estado</th>
                    <th>Disponíveis</th>
                    <th>Construindo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="b in warmEstados" :key="b.estado">
                    <td><strong>{{ b.estado }}</strong></td>
                    <td>
                      <span :class="b.available > 0 ? 'badge-ok' : 'badge-warn'">{{ b.available }}</span>
                    </td>
                    <td>
                      <span v-if="b.inflight" class="badge-warn">aquecendo…</span>
                      <span v-else class="warm-idle">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'

const STORAGE_KEY = 'painel_token'
const API = (path) => `/api${path}`
const SWAL_COMMON = {
  confirmButtonColor: '#1769aa',
  cancelButtonColor: '#667589',
  buttonsStyling: true,
  reverseButtons: true,
}
// Notificação rápida (toast) — não bloqueia, auto-fecha. Usada nas ações de
// infra (PM2/nginx) que rodam sem confirmação prévia.
const SWAL_TOAST = {
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 2500,
  timerProgressBar: true,
}

export default {
  name: 'PainelAdmin',
  data() {
    return {
      tokenInput:     '',
      autenticado:    false,
      erroLogin:      '',
      carregando:     false,
      abaAtiva:        'resumo',

      projetos:        [],
      carregandoProj: false,
      erroProj:       '',
      criandoProjeto: false,
      novoProjeto:    { name: '', original_name: '' },

      pm2:            [],
      carregandoPm2:  false,
      erroPm2:        '',
      // 'pm2' | 'nginx' | '' — controla o disabled/spinner dos botões de infra.
      infraBusy:      '',
      // Stats do warm pool OTP (vem de /api/equatorial/health).
      warmOtp:        null,
      carregandoWarm: false,
      erroWarm:       '',
      carregandoAdmin: false,
      erroAdmin:       '',
      ultimaAtualizacao: null,
      autoRefreshTimer: null,
      metricas:        { visitas: 0, pix_gerados: 0, pix_copiados: 0, acessos: 0 },
      pixGerados:      [],
      pixCopiados:     [],
      acessos:         [],
      historico:       [],

      logSvc:   null,
      logOut:   '',
      logErr:   '',
      logAba:   'out',

      // Gateway PIX
      gatewayAtivo:       'superpay',
      gatewayEscolhido:   'superpay',
      carregandoGateway:  false,
      erroGateway:        '',
      gatewaysDisponiveis: [
        { id: 'superpay', nome: 'SuperPay BR',      descricao: 'Gateway padrão — api.superpaybr.com' },
        { id: 'sigma',    nome: 'SigmaPayments',    descricao: 'Gateway alternativo — api.sigmapayments.com.br' },
        { id: 'chave',    nome: 'Chave PIX',        descricao: 'Recebimento direto na chave cadastrada (BR Code local)' },
      ],

      // Chave PIX estática
      chavePix:        null,
      chavePixInput:   '',
      chavePixNome:    '',
      chavePixCidade:  '',
      editandoChavePix: false,
      carregandoChave: false,
      erroChavePix:    '',

      // Fatura fallback (valores editáveis — aba Gateway PIX)
      fallbackConfig: { contrato: '', valor_min: 140, valor_max: 200 },
      fallbackPadrao: { contrato: '003037680582', valor_min: 140, valor_max: 200 },
      carregandoFallback: false,
      erroFallback: '',

      // Credenciais
      credSuperpayInput:   '',      credSigmaInput:      '',
      credSuperpayHint:    '',
      credSigmaHint:       '',
      credSuperpayVisivel: false,
      credSigmaVisivel:    false,
      carregandoCredenciais: false,
      erroCredenciais:     '',
    }
  },
  computed: {
    token() {
      return sessionStorage.getItem(STORAGE_KEY) || ''
    },
    navItems() {
      return [
        { id: 'resumo', label: 'Dashboard', count: this.metricas.visitas },
        { id: 'projetos', label: 'Projetos', count: this.projetos.length },
        { id: 'pix-gerados', label: 'PIX gerado', count: this.metricas.pix_gerados },
        { id: 'pix-copiados', label: 'PIX copiado', count: this.metricas.pix_copiados },
        { id: 'acessos', label: 'Acessos', count: this.metricas.acessos },
        { id: 'historico', label: 'Histórico', count: this.historico.length },
        { id: 'gateway', label: 'Gateway PIX', count: null },
        { id: 'pm2', label: 'PM2', count: this.pm2.length },
        { id: 'warm', label: 'Pool OTP', count: this.warmTotalAvailable },
      ]
    },
    warmTotalAvailable() {
      const buckets = this.warmOtp?.buckets || {}
      return Object.values(buckets).reduce((sum, b) => sum + (b?.available || 0), 0)
    },
    warmEstados() {
      const buckets = this.warmOtp?.buckets || {}
      // Mantém ordem fixa por estado (Goiás e RS removidos — não operados)
      const order = ['pa', 'ap', 'ma', 'pi', 'al']
      const keys = Array.from(new Set([...order, ...Object.keys(buckets)]))
      return keys.map(k => ({
        estado: k.toUpperCase(),
        available: buckets[k]?.available || 0,
        inflight: !!buckets[k]?.inflight,
      }))
    },
    warmHitRate() {
      const hit = Number(this.warmOtp?.hit || 0)
      const miss = Number(this.warmOtp?.miss || 0)
      const total = hit + miss
      if (!total) return null
      return Math.round((hit / total) * 100)
    },
    // ─── Dashboard ──────────────────────────────────────────────────────────
    taxaCopia() {
      const g = Number(this.metricas.pix_gerados || 0)
      if (!g) return null
      return Math.round((Number(this.metricas.pix_copiados || 0) / g) * 100)
    },
    pixPendentes() {
      return Math.max(0, Number(this.metricas.pix_gerados || 0) - Number(this.metricas.pix_copiados || 0))
    },
    ticketMedio() {
      const valores = this.pixGerados.map(p => Number(p?.valor)).filter(v => Number.isFinite(v) && v > 0)
      if (!valores.length) return null
      return valores.reduce((a, b) => a + b, 0) / valores.length
    },
    pixPorDia() {
      const keyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      const hoje = new Date()
      hoje.setHours(0, 0, 0, 0)
      const dias = []
      for (let i = 6; i >= 0; i--) {
        const d = new Date(hoje)
        d.setDate(hoje.getDate() - i)
        dias.push({ key: keyOf(d), date: d, value: 0 })
      }
      const porChave = new Map(dias.map(d => [d.key, d]))
      for (const p of this.pixGerados) {
        const t = p?.gerado_em ? new Date(p.gerado_em) : null
        if (!t || Number.isNaN(t.getTime())) continue
        const row = porChave.get(keyOf(t))
        if (row) row.value++
      }
      const max = Math.max(1, ...dias.map(d => d.value))
      const nomes = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']
      return dias.map(d => ({
        key: d.key,
        label: nomes[d.date.getDay()],
        value: d.value,
        pct: Math.round((d.value / max) * 100),
      }))
    },
    acessosRecentes() {
      return this.acessos.slice(0, 6)
    },
    pixRecentes() {
      return this.pixGerados.slice(0, 6)
    },
    pixGeradosPorDia() {
      return this.agruparPorDia(this.pixGerados, p => p.gerado_em)
    },
    pixCopiadosPorDia() {
      return this.agruparPorDia(this.pixCopiados, p => p.copiado_em)
    },
    historicoPorDia() {
      return this.agruparPorDia(this.historico, h => h.pix_copiado || h.pix_gerado)
    },
  },
  created() {
    const saved = sessionStorage.getItem(STORAGE_KEY)
    if (saved) {
      this.autenticado = true
      this.carregarTudo()
      this.iniciarAutoRefresh()
    }
  },
  watch: {
    // Auto-load do warm pool quando entrar na aba (e a cada 5s enquanto fica)
    abaAtiva(novo, antigo) {
      if (novo === 'gateway') { this.carregarGateway(); this.carregarFallback() }
      if (novo === 'warm') {
        this.carregarWarm()
        this._warmTimer = setInterval(() => {
          if (this.abaAtiva === 'warm' && !document.hidden) this.carregarWarm()
        }, 5000)
      } else if (antigo === 'warm' && this._warmTimer) {
        clearInterval(this._warmTimer)
        this._warmTimer = null
      }
    },
  },
  beforeUnmount() {
    if (this._warmTimer) { clearInterval(this._warmTimer); this._warmTimer = null }
    if (this.autoRefreshTimer) { clearInterval(this.autoRefreshTimer); this.autoRefreshTimer = null }
  },
  methods: {
    headers() {
      return { 'Content-Type': 'application/json', Authorization: `Bearer ${this.token}` }
    },

    async entrar() {
      this.erroLogin = ''
      if (!this.tokenInput.trim()) { this.erroLogin = 'Informe o token.'; return }
      this.carregando = true
      try {
        const r = await fetch(API('/projects'), {
          headers: { Authorization: `Bearer ${this.tokenInput.trim()}` },
        })
        if (r.status === 401) { this.erroLogin = 'Token inválido.'; return }
        sessionStorage.setItem(STORAGE_KEY, this.tokenInput.trim())
        this.autenticado = true
        this.tokenInput = ''
        this.carregarTudo()
        this.iniciarAutoRefresh()
      } catch (e) {
        this.erroLogin = e.message
      } finally {
        this.carregando = false
      }
    },

    sair() {
      sessionStorage.removeItem(STORAGE_KEY)
      this.autenticado = false
      if (this.autoRefreshTimer) { clearInterval(this.autoRefreshTimer); this.autoRefreshTimer = null }
      this.projetos = []
      this.pm2 = []
      this.pixGerados = []
      this.pixCopiados = []
      this.acessos = []
      this.historico = []
      this.metricas = { visitas: 0, pix_gerados: 0, pix_copiados: 0, acessos: 0 }
    },

    async carregarTudo() {
      await Promise.all([this.carregarProjetos(), this.carregarPm2(), this.carregarDashboard(), this.carregarGateway()])
    },

    async carregarProjetos() {
      this.carregandoProj = true
      this.erroProj = ''
      try {
        const r = await fetch(API('/projects'), { headers: this.headers() })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        this.projetos = d.projects || []
      } catch (e) {
        this.erroProj = e.message
      } finally {
        this.carregandoProj = false
      }
    },

    abrirCriar() {
      this.criandoProjeto = true
      this.novoProjeto = { name: '', original_name: '' }
    },

    async criarProjeto() {
      const projeto = {
        name: this.novoProjeto.name.trim(),
        original_name: this.novoProjeto.original_name.trim(),
      }

      if (!projeto.name) {
        this.erroProj = 'Nome obrigatório.'
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'warning',
          title: 'Nome obrigatório',
          text: 'Informe o nome interno do projeto antes de criar.',
          confirmButtonText: 'Entendi',
          showCancelButton: false,
        })
        return
      }

      const confirmado = await Swal.fire({
        ...SWAL_COMMON,
        icon: 'question',
        title: 'Criar novo projeto?',
        text: `O projeto "${projeto.name}" será criado no painel.`,
        showCancelButton: true,
        confirmButtonText: 'Criar projeto',
        cancelButtonText: 'Cancelar',
      })

      if (!confirmado.isConfirmed) return

      this.carregando = true
      this.erroProj = ''
      try {
        const r = await fetch(API('/projects'), {
          method: 'POST',
          headers: this.headers(),
          body: JSON.stringify(projeto),
        })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        this.criandoProjeto = false
        await this.carregarProjetos()
        await this.carregarDashboard()
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'success',
          title: 'Projeto criado',
          text: `O projeto "${projeto.name}" foi criado com sucesso.`,
          timer: 1800,
          showConfirmButton: false,
        })
      } catch (e) {
        this.erroProj = e.message
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'error',
          title: 'Erro ao criar',
          text: e.message || 'Não foi possível criar o projeto.',
          confirmButtonText: 'Entendi',
          showCancelButton: false,
        })
      } finally {
        this.carregando = false
      }
    },

    async deletarProjeto(name) {
      const confirmado = await Swal.fire({
        ...SWAL_COMMON,
        icon: 'warning',
        title: 'Deletar projeto?',
        text: `O projeto "${name}" será removido junto com os arquivos.`,
        showCancelButton: true,
        confirmButtonColor: '#b42318',
        confirmButtonText: 'Deletar',
        cancelButtonText: 'Cancelar',
      })

      if (!confirmado.isConfirmed) return

      this.erroProj = ''
      try {
        const r = await fetch(API(`/projects/${name}`), {
          method: 'DELETE',
          headers: this.headers(),
        })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        await this.carregarProjetos()
        await this.carregarDashboard()
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'success',
          title: 'Projeto deletado',
          text: `O projeto "${name}" foi removido.`,
          timer: 1800,
          showConfirmButton: false,
        })
      } catch (e) {
        this.erroProj = e.message
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'error',
          title: 'Erro ao deletar',
          text: e.message || 'Não foi possível deletar o projeto.',
          confirmButtonText: 'Entendi',
          showCancelButton: false,
        })
      }
    },

    async carregarPm2() {
      this.carregandoPm2 = true
      this.erroPm2 = ''
      try {
        const r = await fetch(API('/pm2'), { headers: this.headers() })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        this.pm2 = d.services || []
      } catch (e) {
        this.erroPm2 = e.message
      } finally {
        this.carregandoPm2 = false
      }
    },

    async carregarDashboard() {
      this.carregandoAdmin = true
      this.erroAdmin = ''
      try {
        const r = await fetch(API('/admin/dashboard?limit=200'), { headers: this.headers() })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        this.metricas = {
          visitas: Number(d.metrics?.visitas || 0),
          pix_gerados: Number(d.metrics?.pix_gerados || 0),
          pix_copiados: Number(d.metrics?.pix_copiados || 0),
          acessos: Number(d.metrics?.acessos || 0),
        }
        this.pixGerados = d.pix_gerados || []
        this.pixCopiados = d.pix_copiados || []
        this.acessos = d.acessos || []
        this.historico = d.historico || []
        this.ultimaAtualizacao = new Date()
      } catch (e) {
        this.erroAdmin = e.message
      } finally {
        this.carregandoAdmin = false
      }
    },

    iniciarAutoRefresh() {
      if (this.autoRefreshTimer) clearInterval(this.autoRefreshTimer)
      this.autoRefreshTimer = setInterval(() => {
        if (!this.autenticado) return
        if (document.hidden) return
        this.carregarDashboard().catch(() => {})
        if (this.abaAtiva === 'pm2') this.carregarPm2().catch(() => {})
        if (this.abaAtiva === 'warm') this.carregarWarm().catch(() => {})
        if (this.abaAtiva === 'gateway') this.carregarGateway().catch(() => {})
      }, 15000)
    },

    async zerarContadores() {
      const confirmado = await Swal.fire({
        ...SWAL_COMMON,
        icon: 'warning',
        title: 'Zerar contadores?',
        text: 'Visitas, acessos, PIX gerados e copiados voltam a zero. Os códigos PIX já emitidos continuam válidos.',
        showCancelButton: true,
        confirmButtonColor: '#b3392f',
        confirmButtonText: 'Zerar',
        cancelButtonText: 'Cancelar',
      })

      if (!confirmado.isConfirmed) return

      this.erroAdmin = ''
      try {
        const r = await fetch(API('/admin/metrics/reset'), { method: 'POST', headers: this.headers() })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error || 'falha ao zerar contadores')
        await this.carregarDashboard()
        await Swal.fire({ ...SWAL_TOAST, icon: 'success', title: 'Contadores zerados' })
      } catch (e) {
        this.erroAdmin = e.message
      }
    },

    async limparVisitas() {
      const confirmado = await Swal.fire({
        ...SWAL_COMMON,
        icon: 'warning',
        title: 'Limpar visitas?',
        text: 'O contador e a lista de acessos serão zerados.',
        showCancelButton: true,
        confirmButtonColor: '#b42318',
        confirmButtonText: 'Limpar',
        cancelButtonText: 'Cancelar',
      })

      if (!confirmado.isConfirmed) return

      this.carregandoAdmin = true
      this.erroAdmin = ''
      try {
        const r = await fetch(API('/admin/visits'), {
          method: 'DELETE',
          headers: this.headers(),
        })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        await this.carregarDashboard()
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'success',
          title: 'Visitas limpas',
          text: 'O contador de visitas foi zerado.',
          timer: 1600,
          showConfirmButton: false,
        })
      } catch (e) {
        this.erroAdmin = e.message
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'error',
          title: 'Erro ao limpar',
          text: e.message || 'Não foi possível limpar as visitas.',
          confirmButtonText: 'Entendi',
          showCancelButton: false,
        })
      } finally {
        this.carregandoAdmin = false
      }
    },

    async pm2Action(name, action) {
      this.erroPm2 = ''
      try {
        const r = await fetch(API(`/pm2/${name}/${action}`), {
          method: 'POST',
          headers: this.headers(),
        })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        setTimeout(() => this.carregarPm2(), 1200)
      } catch (e) {
        this.erroPm2 = e.message
      }
    },

    async restartPm2All() {
      // Sem confirmação — executa direto e mostra um toast rápido (pedido do
      // usuário: ações de infra precisam ser ágeis).
      this.erroPm2 = ''
      this.infraBusy = 'pm2'
      try {
        const r = await fetch(API('/pm2/all/restart'), {
          method: 'POST',
          headers: this.headers(),
        })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error || 'Falha ao reiniciar PM2')
        Swal.fire({
          ...SWAL_TOAST,
          icon: 'success',
          title: 'PM2 reiniciado',
        })
        // Dá tempo do PM2 subir antes de re-listar.
        setTimeout(() => this.carregarPm2(), 2500)
      } catch (e) {
        this.erroPm2 = e.message
        Swal.fire({
          ...SWAL_TOAST,
          icon: 'error',
          title: 'Erro ao reiniciar PM2',
          text: e.message,
          timer: 4000,
        })
      } finally {
        this.infraBusy = ''
      }
    },

    async restartNginx() {
      // Sem confirmação — executa direto e mostra um toast rápido.
      this.erroPm2 = ''
      this.infraBusy = 'nginx'
      try {
        const r = await fetch(API('/nginx/restart'), {
          method: 'POST',
          headers: this.headers(),
        })
        // Mesmo que o nginx caia no meio do response, o backend já respondeu
        // antes do restart porque é fire-and-forget.
        const d = await r.json()
        if (!d.ok) throw new Error(d.error || 'Falha ao reiniciar nginx')
        Swal.fire({
          ...SWAL_TOAST,
          icon: 'success',
          title: 'Nginx reiniciado',
        })
      } catch (e) {
        this.erroPm2 = e.message
        Swal.fire({
          ...SWAL_TOAST,
          icon: 'error',
          title: 'Erro ao reiniciar nginx',
          text: e.message,
          timer: 4000,
        })
      } finally {
        this.infraBusy = ''
      }
    },

    async carregarGateway() {
      this.erroGateway = ''
      this.carregandoGateway = true
      try {
        const r = await fetch(API('/equatorial/pix/gateway'), { headers: this.headers() })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        this.gatewayAtivo     = d.gateway
        this.gatewayEscolhido = d.gateway
      } catch (e) {
        this.erroGateway = e.message
      } finally {
        this.carregandoGateway = false
      }
      // Carrega credenciais junto
      await this.carregarCredenciais()
      await this.carregarChavePix()
    },

    // ── Fatura fallback (valores) ────────────────────────────────────────────
    async carregarFallback() {
      this.erroFallback = ''
      try {
        const r = await fetch(API('/equatorial/pix/fallback'), { headers: this.headers() })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error || 'falha ao carregar')
        this.fallbackConfig = { ...this.fallbackConfig, ...d.config }
        if (d.defaults) this.fallbackPadrao = { ...d.defaults }
      } catch (e) {
        this.erroFallback = e.message
      }
    },

    async salvarFallback() {
      this.erroFallback = ''
      const min = Number(this.fallbackConfig.valor_min)
      const max = Number(this.fallbackConfig.valor_max)
      if (!(min > 0) || !(max > 0)) { this.erroFallback = 'Informe valores maiores que zero.'; return }
      if (min > max) { this.erroFallback = 'O valor mínimo não pode ser maior que o máximo.'; return }
      this.carregandoFallback = true
      try {
        const r = await fetch(API('/equatorial/pix/fallback'), {
          method: 'POST',
          headers: this.headers(),
          body: JSON.stringify({
            valor_min: min,
            valor_max: max,
            contrato: this.fallbackConfig.contrato,
          }),
        })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error || 'falha ao salvar')
        this.fallbackConfig = { ...this.fallbackConfig, ...d.config }
        await Swal.fire({ ...SWAL_TOAST, icon: 'success', title: 'Valores do fallback atualizados' })
      } catch (e) {
        this.erroFallback = e.message
      } finally {
        this.carregandoFallback = false
      }
    },

    async restaurarFallbackPadrao() {
      this.fallbackConfig = {
        ...this.fallbackConfig,
        valor_min: Number(this.fallbackPadrao.valor_min) || 140,
        valor_max: Number(this.fallbackPadrao.valor_max) || 200,
        contrato: this.fallbackPadrao.contrato || '',
      }
      await this.salvarFallback()
    },

    // ── Chave PIX ────────────────────────────────────────────────────────────
    async carregarChavePix() {
      this.erroChavePix = ''
      try {
        const r = await fetch(API('/equatorial/pix/chave'), { headers: this.headers() })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        this.chavePix = d.chave || null
        if (this.chavePix) {
          this.chavePixInput  = this.chavePix.chave
          this.chavePixNome   = this.chavePix.nome
          this.chavePixCidade = this.chavePix.cidade
        } else {
          this.chavePixInput  = ''
          this.chavePixNome   = ''
          this.chavePixCidade = ''
        }
        this.editandoChavePix = false
      } catch (e) {
        this.erroChavePix = e.message
      }
    },

    iniciarEdicaoChave() {
      this.erroChavePix = ''
      this.editandoChavePix = true
    },

    cancelarEdicaoChave() {
      this.erroChavePix = ''
      this.editandoChavePix = false
      this.chavePixInput  = this.chavePix?.chave || ''
      this.chavePixNome   = this.chavePix?.nome  || ''
      this.chavePixCidade = this.chavePix?.cidade || ''
    },

    async salvarChavePix() {
      this.erroChavePix = ''
      const chave = this.chavePixInput.trim()
      if (!chave) {
        this.erroChavePix = 'Informe a chave PIX.'
        return
      }
      this.carregandoChave = true
      try {
        const r = await fetch(API('/equatorial/pix/chave'), {
          method: 'POST',
          headers: this.headers(),
          body: JSON.stringify({
            chave,
            nome:   this.chavePixNome.trim(),
            cidade: this.chavePixCidade.trim(),
          }),
        })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        await this.carregarChavePix()
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'success',
          title: this.chavePix ? 'Chave PIX salva' : 'Chave PIX cadastrada',
          text: `Cobranças por chave usarão: ${d.chave?.chave || chave}`,
          timer: 2200,
          showConfirmButton: false,
        })
      } catch (e) {
        this.erroChavePix = e.message
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'error',
          title: 'Erro ao salvar chave',
          text: e.message || 'Não foi possível salvar a chave PIX.',
          confirmButtonText: 'Entendi',
          showCancelButton: false,
        })
      } finally {
        this.carregandoChave = false
      }
    },

    async removerChavePix() {
      this.erroChavePix = ''
      const confirm = await Swal.fire({
        ...SWAL_COMMON,
        icon: 'warning',
        title: 'Remover chave PIX?',
        text: 'As cobranças por chave deixarão de funcionar até cadastrar outra.',
        showCancelButton: true,
        confirmButtonText: 'Remover',
        cancelButtonText: 'Cancelar',
      })
      if (!confirm.isConfirmed) return

      this.carregandoChave = true
      try {
        const r = await fetch(API('/equatorial/pix/chave'), {
          method: 'DELETE',
          headers: this.headers(),
        })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        await this.carregarChavePix()
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'success',
          title: 'Chave removida',
          timer: 1800,
          showConfirmButton: false,
        })
      } catch (e) {
        this.erroChavePix = e.message
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'error',
          title: 'Erro ao remover',
          text: e.message || 'Não foi possível remover a chave PIX.',
          confirmButtonText: 'Entendi',
          showCancelButton: false,
        })
      } finally {
        this.carregandoChave = false
      }
    },

    async carregarCredenciais() {
      this.erroCredenciais = ''
      try {
        const r = await fetch(API('/equatorial/pix/credentials'), { headers: this.headers() })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        this.credSuperpayHint = d.superpay_auth_hint || ''
        this.credSigmaHint    = d.sigma_key_hint     || ''
        // Limpa inputs ao recarregar (não pré-preenche por segurança)
        this.credSuperpayInput = ''
        this.credSigmaInput    = ''
      } catch (e) {
        this.erroCredenciais = e.message
      }
    },

    async salvarCredenciais() {
      this.erroCredenciais = ''
      this.carregandoCredenciais = true
      const body = {}
      if (this.credSuperpayInput.trim() !== '') body.superpay_auth = this.credSuperpayInput.trim()
      if (this.credSigmaInput.trim()    !== '') body.sigma_key     = this.credSigmaInput.trim()

      if (!Object.keys(body).length) {
        this.erroCredenciais = 'Preencha ao menos um campo para salvar.'
        this.carregandoCredenciais = false
        return
      }

      try {
        const r = await fetch(API('/equatorial/pix/credentials'), {
          method: 'POST',
          headers: this.headers(),
          body: JSON.stringify(body),
        })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        await this.carregarCredenciais()
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'success',
          title: 'Credenciais salvas',
          text: 'As novas chaves entram em vigor para as próximas cobranças PIX.',
          timer: 2000,
          showConfirmButton: false,
        })
      } catch (e) {
        this.erroCredenciais = e.message
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'error',
          title: 'Erro ao salvar',
          text: e.message || 'Não foi possível salvar as credenciais.',
          confirmButtonText: 'Entendi',
          showCancelButton: false,
        })
      } finally {
        this.carregandoCredenciais = false
      }
    },

    async salvarGateway() {
      if (this.gatewayEscolhido === this.gatewayAtivo) return
      this.erroGateway = ''
      this.carregandoGateway = true
      try {
        const r = await fetch(API('/equatorial/pix/gateway'), {
          method: 'POST',
          headers: this.headers(),
          body: JSON.stringify({ gateway: this.gatewayEscolhido }),
        })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        this.gatewayAtivo = d.gateway
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'success',
          title: 'Gateway atualizado',
          text: `Novas cobranças PIX usarão: ${({ sigma: 'SigmaPayments', chave: 'Chave PIX' }[d.gateway]) || 'SuperPay BR'}`,
          timer: 2000,
          showConfirmButton: false,
        })
      } catch (e) {
        this.erroGateway = e.message
        await Swal.fire({
          ...SWAL_COMMON,
          icon: 'error',
          title: 'Erro ao salvar',
          text: e.message || 'Não foi possível alterar o gateway.',
          confirmButtonText: 'Entendi',
          showCancelButton: false,
        })
      } finally {
        this.carregandoGateway = false
      }
    },

    /**
     * Carrega stats do pool OTP a partir do /api/equatorial/health.
     * Endpoint é público (sem auth) — não precisa de token de admin.
     */
    async carregarWarm() {
      this.erroWarm = ''
      this.carregandoWarm = true
      try {
        const r = await fetch('/api/equatorial/health')
        const d = await r.json()
        this.warmOtp = d?.warm_otp || null
      } catch (e) {
        this.erroWarm = e.message
      } finally {
        this.carregandoWarm = false
      }
    },

    async verLog(name) {
      this.logSvc = name
      this.logOut = ''
      this.logErr = ''
      this.logAba = 'out'
      try {
        const r = await fetch(API(`/pm2/logs?name=${encodeURIComponent(name)}&lines=200`), {
          headers: this.headers(),
        })
        const d = await r.json()
        if (!d.ok) throw new Error(d.error)
        this.logOut = d.logs?.out || '(vazio)'
        this.logErr = d.logs?.err || '(vazio)'
      } catch (e) {
        this.logOut = e.message
      }
    },

    statusClass(s) {
      if (s === 'online') return 'badge-ok'
      if (s === 'stopped') return 'badge-no'
      return 'badge-warn'
    },

    formatNumber(value) {
      return new Intl.NumberFormat('pt-BR').format(Number(value || 0))
    },

    formatCurrency(value) {
      const n = Number(value || 0)
      if (!Number.isFinite(n)) return '-'
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n)
    },

    formatDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    },

    accessLabel(type) {
      if (type === 'site_access') return 'site'
      if (type === 'pix_checkout') return 'pix'
      if (type === 'pix_copiado') return 'copiado'
      return type || 'acesso'
    },

    historyLabel(item) {
      if (item.type === 'pix_gerado') return 'PIX gerado'
      if (item.type === 'pix_copiado') return 'PIX copiado'
      if (item.type === 'site_access') return 'Acesso site'
      if (item.type === 'pix_checkout') return 'Acesso PIX'
      return item.title || item.type || 'Evento'
    },

    historyBadgeClass(type) {
      if (type === 'pix_gerado') return 'badge-warn'
      if (type === 'pix_copiado') return 'badge-ok'
      return 'badge-soft'
    },

    // ── Agrupamento por data (histórico / PIX gerados / PIX copiados) ────────
    diaKey(value) {
      const d = value ? new Date(value) : null
      if (!d || Number.isNaN(d.getTime())) return 'sem-data'
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },

    formatDiaLabel(value) {
      const d = value ? new Date(value) : null
      if (!d || Number.isNaN(d.getTime())) return 'Sem data'
      const semana = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][d.getDay()]
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} · ${semana}`
    },

    agruparPorDia(lista, getData) {
      const grupos = new Map()
      for (const item of Array.isArray(lista) ? lista : []) {
        const valor = getData(item)
        const key = this.diaKey(valor)
        if (!grupos.has(key)) {
          grupos.set(key, { key, label: this.formatDiaLabel(valor), itens: [] })
        }
        grupos.get(key).itens.push(item)
      }
      return Array.from(grupos.values())
    },

    generatorDataLines(item) {
      const data = item?.dados_gerador || {}
      const rows = [
        ['Nome', data.nome],
        ['Documento', data.documento],
        ['Contrato', data.contrato],
        ['Nascimento', data.nascimento],
        ['RG (3 dígitos)', data.rg],
        ['Mãe', data.mae],
        ['Estado', data.estado],
        ['Referência', data.referencia || item?.referencia],
        ['Vencimento', data.vencimento || item?.vencimento],
        ['Origem', data.origem],
      ]
      const filled = rows
        .filter(([, value]) => String(value || '').trim())
        .map(([label, value]) => ({ label, value }))
      return filled.length ? filled : [{ label: 'Chave', value: item?.key || '-' }]
    },
  },
}
</script>

<style scoped>
.painel-wrap {
  /* ── Claymorphism — tokens ─────────────────────────────────────────────── */
  --panel-bg: #eef2fa;
  --panel-surface: #f7f9fe;
  --panel-card: #f4f7fd;
  --panel-card-hover: #f8fbff;
  --panel-surface-muted: #eef3fb;
  --panel-border: rgba(148, 166, 196, .30);
  --panel-border-strong: rgba(148, 166, 196, .45);
  --panel-text: #2a3350;
  --panel-muted: #5b6884;
  --panel-subtle: #69768f;
  --panel-primary: #3d51d4;
  --panel-primary-hover: #3243b8;
  --panel-primary-soft: #e8ecff;
  --panel-danger: #b3392f;
  --panel-danger-soft: #ffecea;
  --panel-warning: #8f530c;
  --panel-warning-soft: #fff4e2;
  --panel-success: #1c7a55;
  --panel-success-soft: #e6f7ef;
  --panel-shadow: 0 18px 38px rgba(148, 166, 196, .28);
  --clay: 0 20px 40px rgba(148, 166, 196, .30),
          inset 0 -7px 16px rgba(148, 166, 196, .22),
          inset 0 7px 16px rgba(255, 255, 255, .95);
  --clay-sm: 0 10px 22px rgba(148, 166, 196, .26),
             inset 0 -4px 10px rgba(148, 166, 196, .18),
             inset 0 4px 10px rgba(255, 255, 255, .90);
  --clay-press: inset 0 5px 12px rgba(148, 166, 196, .35),
                inset 0 -4px 10px rgba(255, 255, 255, .75);

  min-height: 100vh;
  background: var(--panel-bg);
  color: var(--panel-text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
  isolation: isolate;
}

.painel-wrap,
.painel-wrap * {
  box-sizing: border-box;
}

.painel-wrap button,
.painel-wrap input {
  font: inherit;
}

.painel-wrap button {
  appearance: none;
  line-height: 1;
  white-space: nowrap;
}

.painel-wrap button:disabled {
  cursor: not-allowed;
  opacity: .62;
}

.painel-wrap button:focus-visible,
.painel-wrap input:focus-visible,
.painel-wrap a:focus-visible {
  outline: 3px solid rgba(23, 105, 170, .22);
  outline-offset: 2px;
}

/* Login */
.login-card {
  width: min(390px, calc(100vw - 32px));
  margin: 112px auto 0;
  padding: 30px;
  text-align: center;
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  box-shadow: var(--panel-shadow);
}

.login-card h2 {
  margin: 0 0 22px;
  color: var(--panel-text);
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 0;
}

.login-card input {
  width: 100%;
  height: 44px;
  padding: 0 13px;
  margin-bottom: 12px;
  color: var(--panel-text);
  background: #fff;
  border: 1px solid var(--panel-border-strong);
  border-radius: 7px;
  font-size: .95rem;
}

.login-card input::placeholder {
  color: var(--panel-subtle);
}

.login-card button {
  width: 100%;
  height: 44px;
  color: #fff;
  background: var(--panel-primary);
  border: 1px solid var(--panel-primary);
  border-radius: 7px;
  font-size: .95rem;
  font-weight: 700;
  cursor: pointer;
}

.login-card button:hover:not(:disabled) {
  background: var(--panel-primary-hover);
  border-color: var(--panel-primary-hover);
}

/* Header */
.painel-header {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 28px;
  background: var(--panel-surface);
  border-bottom: 1px solid var(--panel-border);
  box-shadow: 0 1px 0 rgba(24, 34, 52, .04);
}

.painel-header span {
  color: var(--panel-text);
  font-size: 1rem;
  font-weight: 700;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.visit-counter {
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  padding: 0 12px;
  color: var(--panel-primary) !important;
  background: var(--panel-primary-soft);
  border: 1px solid #c5dff1;
  border-radius: 999px;
  font-size: .84rem !important;
  font-weight: 800 !important;
}

.btn-sair {
  height: 34px;
  padding: 0 14px;
  color: var(--panel-muted);
  background: #fff;
  border: 1px solid var(--panel-border-strong);
  border-radius: 7px;
  cursor: pointer;
  font-size: .82rem;
  font-weight: 700;
}

.btn-sair:hover {
  color: var(--panel-danger);
  background: var(--panel-danger-soft);
  border-color: #f0b8b2;
}

/* Body */
.painel-shell {
  display: grid;
  grid-template-columns: 226px minmax(0, 1fr);
  min-height: calc(100vh - 58px);
}

.painel-nav {
  position: sticky;
  top: 58px;
  align-self: start;
  height: calc(100vh - 58px);
  padding: 18px 12px;
  background: #dfeaf5;
  border-right: 1px solid var(--panel-border);
}

.nav-item {
  width: 100%;
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
  padding: 0 11px;
  color: var(--panel-muted);
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: .9rem;
  font-weight: 800;
  text-align: left;
}

.nav-item:hover {
  color: var(--panel-text);
  background: #eef5fb;
  border-color: var(--panel-border);
}

.nav-item strong {
  min-width: 28px;
  min-height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 7px;
  color: var(--panel-muted);
  background: rgba(255, 255, 255, .75);
  border: 1px solid var(--panel-border);
  border-radius: 999px;
  font-size: .72rem;
}

.nav-item-ativa {
  color: #fff;
  background: var(--panel-primary);
  border-color: var(--panel-primary);
  box-shadow: 0 8px 18px rgba(23, 105, 170, .22);
}

.nav-item-ativa strong {
  color: var(--panel-primary);
  background: #fff;
  border-color: #fff;
}

.painel-body {
  width: min(1180px, calc(100vw - 226px));
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* Card */
.card {
  padding: 20px;
  background: var(--panel-card);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(24, 34, 52, .06);
}

.card:hover {
  background: var(--panel-card-hover);
  border-color: var(--panel-border-strong);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
}

.card-header h3 {
  margin: 0;
  color: var(--panel-text);
  font-size: 1rem;
  font-weight: 700;
}

/* Card de ações de infra (pm2 restart all + nginx restart).
   Visual de "zona perigosa": faixa âmbar discreta com botões destacados. */
/* Warm pool dashboard */
.warm-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}
.warm-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  text-align: center;
}
.warm-metric-num {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--panel-text);
  line-height: 1.1;
}
.warm-metric-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--panel-text-muted, #667589);
  margin-top: 4px;
}
.warm-metric-ok    { background: rgba(46, 160, 67, 0.08); border-color: rgba(46, 160, 67, 0.22); }
.warm-metric-warn  { background: rgba(255, 152, 0, 0.08); border-color: rgba(255, 152, 0, 0.22); }
.warm-metric-bad   { background: rgba(218, 54, 51, 0.08); border-color: rgba(218, 54, 51, 0.22); }
.warm-metric-rate  { background: rgba(23, 105, 170, 0.08); border-color: rgba(23, 105, 170, 0.22); }
.warm-ttl {
  font-size: 0.85rem;
  color: var(--panel-text-muted, #667589);
  margin: 0 0 12px;
}
.warm-tabela td:nth-child(1) { width: 90px; }
.warm-idle { color: var(--panel-text-muted, #667589); opacity: 0.5; }

.infra-actions {
  display: grid;
  gap: 10px;
  padding: 14px;
  margin-bottom: 16px;
  background: linear-gradient(180deg, rgba(255, 193, 7, 0.06), rgba(255, 193, 7, 0.02));
  border: 1px solid rgba(255, 152, 0, 0.28);
  border-radius: 10px;
}
.infra-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
}
.infra-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 240px;
}
.infra-text strong {
  color: var(--panel-text);
  font-size: 0.92rem;
}
.infra-text span {
  color: var(--panel-text-muted, #667589);
  font-size: 0.78rem;
}
.infra-text code {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 4px;
  border-radius: 4px;
  font-size: 0.75rem;
}
.infra-action button {
  white-space: nowrap;
  min-width: 150px;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  gap: 12px;
}

/* Hint de auto-refresh */
.auto-refresh-hint {
  margin: 2px 0 8px;
  font-size: 11px;
  color: var(--panel-subtle, #8a98aa);
  text-align: right;
}

.stat-card {
  min-height: 104px;
  padding: 17px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #e5eef8;
  border: 1px solid var(--panel-border-strong);
  border-radius: 8px;
}

.stat-card span {
  color: var(--panel-muted);
  font-size: .82rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0;
}

.stat-card strong {
  color: var(--panel-text);
  font-size: 1.9rem;
  font-weight: 800;
  line-height: 1;
}

/* Form novo projeto */
.form-novo {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(180px, 1fr) auto;
  gap: 10px;
  margin-bottom: 14px;
  padding: 12px;
  background: var(--panel-surface-muted);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
}

.form-novo input {
  width: 100%;
  height: 38px;
  padding: 0 11px;
  color: var(--panel-text);
  background: #fff;
  border: 1px solid var(--panel-border-strong);
  border-radius: 7px;
  font-size: .875rem;
}

.form-novo input::placeholder {
  color: var(--panel-subtle);
}

.form-novo-btns {
  display: flex;
  gap: 8px;
}

/* Tabela */
.tabela {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  overflow: hidden;
  color: var(--panel-text);
  font-size: .875rem;
}

.tabela th {
  padding: 10px 12px;
  text-align: left;
  color: var(--panel-muted);
  background: var(--panel-surface-muted);
  border-top: 1px solid var(--panel-border);
  border-bottom: 1px solid var(--panel-border);
  font-size: .76rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0;
}

.tabela th:first-child {
  border-left: 1px solid var(--panel-border);
  border-top-left-radius: 7px;
}

.tabela th:last-child {
  border-right: 1px solid var(--panel-border);
  border-top-right-radius: 7px;
}

.tabela td {
  padding: 12px;
  border-bottom: 1px solid var(--panel-border);
  vertical-align: middle;
}

.tabela tr:hover td {
  background: #dce9f5;
}

/* Badges */
.badge-ok,
.badge-no,
.badge-warn {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: .75rem;
  font-weight: 800;
}

.badge-ok {
  color: var(--panel-success);
  background: var(--panel-success-soft);
  border: 1px solid #bfe7d2;
}

.badge-no {
  color: var(--panel-danger);
  background: var(--panel-danger-soft);
  border: 1px solid #f2c2bc;
}

.badge-warn {
  color: var(--panel-warning);
  background: var(--panel-warning-soft);
  border: 1px solid #f2d49b;
}

.badge-soft {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 9px;
  color: var(--panel-primary);
  background: var(--panel-primary-soft);
  border: 1px solid #c5dff1;
  border-radius: 999px;
  font-size: .75rem;
  font-weight: 800;
}

/* Botões */
.btn-sm,
.aba,
.aba-ativa {
  min-height: 34px;
  padding: 0 12px;
  border-radius: 7px;
  font-size: .8rem;
  font-weight: 800;
  cursor: pointer;
}

.btn-sm {
  color: #fff;
  background: var(--panel-primary);
  border: 1px solid var(--panel-primary);
}

.btn-sm:hover:not(:disabled) {
  background: var(--panel-primary-hover);
  border-color: var(--panel-primary-hover);
}

.btn-warn {
  color: var(--panel-warning);
  background: var(--panel-warning-soft);
  border-color: #efca87;
}

.btn-warn:hover:not(:disabled) {
  background: #ffedc2;
  border-color: #dda64e;
}

.btn-cancel {
  color: var(--panel-muted);
  background: #fff;
  border-color: var(--panel-border-strong);
}

.btn-cancel:hover:not(:disabled) {
  color: var(--panel-text);
  background: var(--panel-surface-muted);
}

.btn-del {
  width: 32px;
  height: 32px;
  padding: 0;
  color: var(--panel-danger);
  background: #fff;
  border: 1px solid transparent;
  border-radius: 7px;
  cursor: pointer;
  font-size: .95rem;
  font-weight: 800;
}

.btn-del:hover {
  background: var(--panel-danger-soft);
  border-color: #f0b8b2;
}

.acoes {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
}

.link {
  color: var(--panel-primary);
  font-weight: 700;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.mono {
  max-width: 340px;
  color: #425166;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: .78rem;
  white-space: normal;
  word-break: break-all;
}

.path-cell {
  max-width: 420px;
  white-space: normal;
  word-break: break-word;
}

.generator-cell {
  min-width: 240px;
  color: #425166;
  line-height: 1.55;
  white-space: normal;
}

.generator-cell strong {
  color: var(--panel-text);
  font-weight: 800;
}

.erro {
  margin: 10px 0;
  padding: 9px 11px;
  color: var(--panel-danger);
  background: var(--panel-danger-soft);
  border: 1px solid #f2c2bc;
  border-radius: 7px;
  font-size: .85rem;
}

.vazio,
.loading {
  margin: 0;
  padding: 18px 12px;
  color: var(--panel-muted);
  background: var(--panel-surface-muted);
  border: 1px dashed var(--panel-border-strong);
  border-radius: 7px;
  font-size: .875rem;
}

/* Log */
.log-box {
  margin-top: 20px;
  overflow: hidden;
  background: #101827;
  border: 1px solid #263447;
  border-radius: 8px;
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  color: #dbe7f5;
  background: #172235;
  border-bottom: 1px solid #263447;
  font-size: .85rem;
  font-weight: 700;
}

.log-tabs {
  display: flex;
  gap: 0;
  background: #121c2d;
  border-bottom: 1px solid #263447;
}

.aba,
.aba-ativa {
  color: #9fb1c7;
  background: transparent;
  border: 0;
  border-radius: 0;
}

.aba:hover {
  color: #e7eef8;
  background: #1d2a3e;
}

.aba-ativa {
  color: #fff;
  background: #22324a;
  box-shadow: inset 0 -2px 0 #65b5f6;
}

.log-pre {
  max-height: 400px;
  margin: 0;
  padding: 14px;
  overflow-y: auto;
  color: #c6d4e4;
  background: #101827;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: .76rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Gateway PIX */
.gateway-wrap {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.gateway-descricao {
  color: var(--panel-muted);
  font-size: .875rem;
  line-height: 1.55;
}

.gateway-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gateway-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color .15s, background .15s;
}

.gateway-card:hover {
  background: var(--panel-primary-soft);
  border-color: #90c4e8;
}

.gateway-card-ativo {
  background: var(--panel-primary-soft);
  border-color: var(--panel-primary);
  box-shadow: 0 0 0 2px rgba(23, 105, 170, .12);
}

.gateway-radio {
  accent-color: var(--panel-primary);
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
}

.gateway-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.gateway-info strong {
  color: var(--panel-text);
  font-size: .92rem;
}

.gateway-info span {
  color: var(--panel-muted);
  font-size: .78rem;
}

.gateway-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.gateway-hint {
  color: var(--panel-subtle);
  font-size: .8rem;
}

/* Credenciais */
.cred-section {
  padding-top: 20px;
  border-top: 1px solid var(--panel-border);
}

.cred-title {
  margin: 0 0 8px;
  color: var(--panel-text);
  font-size: .92rem;
  font-weight: 700;
}

.cred-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}

.cred-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--panel-text);
  font-size: .84rem;
  font-weight: 700;
}

.cred-hint {
  color: var(--panel-muted);
  font-size: .75rem;
  font-weight: 400;
  font-family: "SFMono-Regular", Consolas, monospace;
}

.cred-input-row {
  display: flex;
  gap: 8px;
}

.cred-input {
  flex: 1;
  height: 38px;
  padding: 0 11px;
  color: var(--panel-text);
  background: #fff;
  border: 1px solid var(--panel-border-strong);
  border-radius: 7px;
  font-size: .875rem;
  font-family: "SFMono-Regular", Consolas, monospace;
  min-width: 0;
}

.cred-input::placeholder {
  color: var(--panel-subtle);
  font-family: inherit;
}

.pixkey-atual {
  padding: 14px 16px;
  background: #f6f7fb;
  border: 1px solid var(--panel-border);
  border-radius: 10px;
}

.pixkey-row {
  display: flex;
  gap: 12px;
  padding: 5px 0;
  font-size: .86rem;
}

.pixkey-label {
  flex: 0 0 130px;
  color: var(--panel-muted);
  font-weight: 700;
}

.pixkey-value {
  color: var(--panel-text);
  word-break: break-all;
}

/* ═══════════════════════════ CLAYMORPHISM THEME ═══════════════════════════ */

.painel-wrap {
  background:
    radial-gradient(900px 520px at 6% -10%, #e3eaff 0%, rgba(227, 234, 255, 0) 62%),
    radial-gradient(760px 460px at 104% 0%, #ffe9f2 0%, rgba(255, 233, 242, 0) 58%),
    radial-gradient(700px 520px at 50% 112%, #e4fbf1 0%, rgba(228, 251, 241, 0) 60%),
    var(--panel-bg);
}

/* Superfícies */
.card,
.login-card {
  background: linear-gradient(160deg, #fbfdff 0%, #edf2fb 100%);
  border: 0;
  border-radius: 32px;
  box-shadow: var(--clay);
}

.card {
  padding: 24px;
}

.card:hover {
  background: linear-gradient(160deg, #ffffff 0%, #f1f6fd 100%);
  border: 0;
}

.login-card {
  border-radius: 36px;
  padding: 34px;
}

.login-card h2 {
  font-size: 1.35rem;
  letter-spacing: -.01em;
}

/* Header */
.painel-header {
  background: linear-gradient(160deg, #fdfeff, #eaf0fa);
  border-bottom: 0;
  box-shadow: 0 14px 28px rgba(148, 166, 196, .20);
}

.btn-sair {
  background: linear-gradient(160deg, #ffffff, #e9eff9);
  border: 0;
  border-radius: 16px;
  box-shadow: var(--clay-sm);
  color: var(--panel-muted);
}

.btn-sair:hover {
  background: linear-gradient(160deg, #fff1ef, #ffe2de);
  border: 0;
  color: var(--panel-danger);
}

.visit-counter {
  background: linear-gradient(160deg, #ffffff, #e6ebff);
  border: 0;
  border-radius: 999px;
  box-shadow: var(--clay-sm);
  color: var(--panel-primary) !important;
  padding: 0 14px;
}

/* Navegação */
.painel-nav {
  background: transparent;
  border-right: 0;
  padding: 18px 14px;
}

.nav-item {
  min-height: 46px;
  margin-bottom: 10px;
  padding: 0 14px;
  border: 0;
  border-radius: 18px;
  background: rgba(255, 255, 255, .55);
  box-shadow: var(--clay-sm);
  color: var(--panel-muted);
}

.nav-item:hover {
  background: #ffffff;
  border: 0;
  color: var(--panel-text);
}

.nav-item strong {
  border: 0;
  background: rgba(255, 255, 255, .85);
  border-radius: 999px;
  box-shadow: inset 0 -2px 4px rgba(148, 166, 196, .20);
  color: var(--panel-muted);
}

.nav-item-ativa {
  background: linear-gradient(160deg, #5a6be6, #3d51d4);
  border: 0;
  border-radius: 18px;
  color: #fff;
  box-shadow: 0 14px 26px rgba(61, 81, 212, .35),
              inset 0 -5px 12px rgba(40, 55, 160, .35),
              inset 0 5px 12px rgba(255, 255, 255, .25);
}

.nav-item-ativa strong {
  background: rgba(255, 255, 255, .92);
  color: var(--panel-primary);
  border: 0;
}

/* Botões */
.btn-sm,
.aba,
.aba-ativa,
.login-card button {
  border: 0;
  border-radius: 16px;
  box-shadow: var(--clay-sm);
}

.btn-sm,
.login-card button {
  background: linear-gradient(160deg, #5a6be6, #3d51d4);
  color: #fff;
}

.btn-sm:hover:not(:disabled),
.login-card button:hover:not(:disabled) {
  background: linear-gradient(160deg, #4a5bdc, #3243b8);
  border: 0;
}

.btn-sm:active:not(:disabled),
.login-card button:active:not(:disabled),
.btn-sair:active {
  box-shadow: var(--clay-press);
  transform: translateY(1px);
}

.btn-sm.btn-warn {
  background: linear-gradient(160deg, #ffe9c4, #ffd79b);
  color: var(--panel-warning);
}

.btn-sm.btn-warn:hover:not(:disabled) {
  background: linear-gradient(160deg, #ffe1b0, #ffcc85);
}

.btn-sm.btn-cancel {
  background: linear-gradient(160deg, #ffffff, #eaf0fa);
  color: var(--panel-muted);
}

.btn-sm.btn-cancel:hover:not(:disabled) {
  background: linear-gradient(160deg, #ffffff, #e2eaf7);
  color: var(--panel-text);
}

.btn-del {
  border: 0;
  border-radius: 13px;
  background: linear-gradient(160deg, #ffffff, #eaf0fa);
  box-shadow: var(--clay-sm);
  color: var(--panel-danger);
}

.btn-del:hover {
  background: linear-gradient(160deg, #fff1ef, #ffe1dd);
  border: 0;
}

.aba,
.aba-ativa {
  background: linear-gradient(160deg, #ffffff, #eaf0fa);
  color: var(--panel-muted);
}

.aba-ativa {
  background: linear-gradient(160deg, #5a6be6, #3d51d4);
  color: #fff;
}

.btn-link {
  padding: 0;
  border: 0;
  background: none;
  box-shadow: none;
  color: var(--panel-primary);
  font-size: .78rem;
  font-weight: 800;
  cursor: pointer;
}

.btn-link:hover {
  text-decoration: underline;
}

/* Inputs */
.login-card input,
.form-novo input,
.cred-input {
  border: 0;
  border-radius: 16px;
  background: #eef3fb;
  color: var(--panel-text);
  box-shadow: inset 0 4px 10px rgba(148, 166, 196, .30),
              inset 0 -3px 8px rgba(255, 255, 255, .90);
}

.login-card input:focus,
.form-novo input:focus,
.cred-input:focus {
  outline: none;
  box-shadow: inset 0 4px 10px rgba(148, 166, 196, .30),
              inset 0 -3px 8px rgba(255, 255, 255, .90),
              0 0 0 3px rgba(61, 81, 212, .22);
}

/* Tabelas */
.tabela th {
  background: transparent;
  border: 0;
  color: var(--panel-muted);
  font-size: .73rem;
  letter-spacing: .04em;
}

.tabela th:first-child,
.tabela th:last-child {
  border: 0;
  border-radius: 0;
}

.tabela td {
  border-bottom: 1px solid rgba(148, 166, 196, .18);
}

.tabela tbody tr:last-child td {
  border-bottom: 0;
}

.tabela tr:hover td {
  background: rgba(255, 255, 255, .72);
}

/* Separador de dia nas tabelas */
.grupo-data td {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(148, 166, 196, .18);
  color: var(--panel-muted);
  background: rgba(255, 255, 255, .55);
  font-size: .74rem;
  font-weight: 800;
  letter-spacing: .05em;
  text-transform: uppercase;
}

.grupo-count {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  margin-left: 8px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, .9);
  box-shadow: inset 0 -2px 4px rgba(148, 166, 196, .22);
  color: var(--panel-primary);
  font-size: .7rem;
}

/* Badges */
.badge-ok,
.badge-no,
.badge-warn,
.badge-soft {
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  box-shadow: inset 0 -2px 5px rgba(148, 166, 196, .22),
              inset 0 2px 5px rgba(255, 255, 255, .95);
}

/* KPI / dashboard */
.dash-head {
  align-items: flex-start;
  margin-bottom: 18px;
}

.dash-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.dash-head h3 {
  font-size: 1.2rem;
  letter-spacing: -.01em;
}

.auto-refresh-hint-left {
  margin: 6px 0 0;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: .74rem;
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--panel-success);
  box-shadow: 0 0 0 4px rgba(28, 122, 85, .16);
  animation: livePulse 2.4s ease-in-out infinite;
}

@keyframes livePulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: .45; }
}

.stat-grid {
  gap: 16px;
}

.stat-grid-3 {
  grid-template-columns: repeat(3, minmax(150px, 1fr));
  margin-top: 16px;
}

.stat-card {
  position: relative;
  overflow: hidden;
  min-height: 134px;
  padding: 20px;
  border: 0;
  border-radius: 26px;
  background: linear-gradient(160deg, #fbfdff, #eaf0fa);
  box-shadow: var(--clay-sm);
}

.stat-card::after {
  content: "";
  position: absolute;
  right: -46px;
  bottom: -62px;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: var(--kpi-glow, rgba(61, 81, 212, .16));
  filter: blur(12px);
  pointer-events: none;
}

.kpi-blue   { --kpi-glow: rgba(61, 81, 212, .20); }
.kpi-violet { --kpi-glow: rgba(124, 77, 255, .20); }
.kpi-mint   { --kpi-glow: rgba(28, 122, 85, .20); }
.kpi-amber  { --kpi-glow: rgba(224, 150, 40, .24); }

.kpi-ghost {
  min-height: 112px;
  background: linear-gradient(160deg, #ffffff, #f2f7fd);
}

.stat-card .kpi-label {
  color: var(--panel-muted);
  font-size: .75rem;
  font-weight: 800;
  letter-spacing: .05em;
  text-transform: uppercase;
}

.stat-card strong {
  font-size: 2rem;
  letter-spacing: -.02em;
  color: var(--panel-text);
}

.stat-card .kpi-money {
  font-size: 1.5rem;
}

.stat-card .kpi-foot {
  color: var(--panel-subtle);
  font-size: .72rem;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

/* Gráfico de barras */
.dash-chart {
  margin-top: 18px;
  padding: 20px 22px 16px;
  border-radius: 26px;
  background: linear-gradient(160deg, #fbfdff, #eef3fb);
  box-shadow: var(--clay-sm);
}

.dash-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.dash-panel-head h4 {
  margin: 0;
  color: var(--panel-text);
  font-size: .95rem;
  font-weight: 800;
}

.dash-note {
  color: var(--panel-subtle);
  font-size: .74rem;
  font-weight: 700;
}

.chart-bars {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  height: 170px;
  padding: 0 2px 4px;
}

.chart-col {
  flex: 1 1 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.chart-value {
  color: var(--panel-muted);
  font-size: .74rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.chart-track {
  flex: 1 1 auto;
  width: 100%;
  max-width: 54px;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  border-radius: 15px;
  background: rgba(148, 166, 196, .16);
  box-shadow: inset 0 3px 8px rgba(148, 166, 196, .24);
}

.chart-fill {
  width: 100%;
  min-height: 6px;
  border-radius: 15px;
  background: linear-gradient(180deg, #6d7cee, #3d51d4);
  box-shadow: inset 0 4px 8px rgba(255, 255, 255, .35);
  transition: height .45s ease;
}

.chart-label {
  color: var(--panel-subtle);
  font-size: .72rem;
  font-weight: 700;
  text-transform: capitalize;
}

/* Painéis e listas compactas */
.dash-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 18px;
}

.dash-panel {
  padding: 18px 20px;
  border-radius: 26px;
  background: linear-gradient(160deg, #fbfdff, #f0f5fc);
  box-shadow: var(--clay-sm);
}

.mini-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.mini-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(148, 166, 196, .16);
}

.mini-list li:last-child {
  border-bottom: 0;
}

.mini-dot {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #8ea0c4;
  box-shadow: 0 0 0 4px rgba(142, 160, 196, .14);
}

.mini-dot-ok {
  background: var(--panel-success);
  box-shadow: 0 0 0 4px rgba(28, 122, 85, .14);
}

.mini-dot-warn {
  background: #c8862a;
  box-shadow: 0 0 0 4px rgba(200, 134, 42, .16);
}

.mini-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.mini-main strong {
  color: var(--panel-text);
  font-size: .85rem;
}

.mini-path {
  overflow: hidden;
  color: var(--panel-subtle);
  font-size: .75rem;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mini-side {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.mini-side span {
  color: var(--panel-muted);
  font-size: .75rem;
  font-weight: 700;
}

.mini-side small {
  color: var(--panel-subtle);
  font-size: .7rem;
}

.mini-empty {
  color: var(--panel-subtle);
  font-size: .82rem;
  padding: 16px 0;
}

/* Blocos internos (gateway, warm, infra, log) */
.warm-metric {
  border: 0;
  border-radius: 22px;
  background: linear-gradient(160deg, #ffffff, #eef3fb);
  box-shadow: var(--clay-sm);
}

.warm-metric-ok   { background: linear-gradient(160deg, #ffffff, #e6f7ef); }
.warm-metric-warn { background: linear-gradient(160deg, #ffffff, #fff4e2); }
.warm-metric-bad  { background: linear-gradient(160deg, #ffffff, #ffecea); }
.warm-metric-rate { background: linear-gradient(160deg, #ffffff, #e8ecff); }

.infra-actions {
  border: 0;
  border-radius: 24px;
  background: linear-gradient(160deg, #fff8ea, #fdefd6);
  box-shadow: var(--clay-sm);
}

.infra-text code {
  background: rgba(148, 166, 196, .22);
  border-radius: 6px;
}

.gateway-card {
  border: 0;
  border-radius: 22px;
  background: linear-gradient(160deg, #ffffff, #f0f5fc);
  box-shadow: var(--clay-sm);
}

.gateway-card:hover {
  border: 0;
  box-shadow: 0 14px 26px rgba(148, 166, 196, .30),
              inset 0 -4px 10px rgba(148, 166, 196, .18),
              inset 0 4px 10px rgba(255, 255, 255, .9);
}

.gateway-card-ativo {
  border: 0;
  box-shadow: 0 0 0 3px rgba(61, 81, 212, .35), var(--clay-sm);
}

.fallback-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.cred-section,
.pixkey-atual,
.log-box {
  border: 0;
  border-radius: 22px;
  background: linear-gradient(160deg, #ffffff, #f1f6fd);
  box-shadow: var(--clay-sm);
}

.log-pre {
  border-radius: 16px;
  background: #20263a;
}

.form-novo {
  border: 0;
  border-radius: 22px;
  background: linear-gradient(160deg, #ffffff, #eef3fb);
  box-shadow: inset 0 3px 10px rgba(148, 166, 196, .20);
}

@media (prefers-reduced-motion: reduce) {
  .live-dot { animation: none; }
  .chart-fill { transition: none; }
}

@media (max-width: 760px) {
  .painel-header {
    padding: 0 16px;
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
    padding-top: 12px;
    padding-bottom: 12px;
  }

  .header-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .painel-shell {
    display: block;
  }

  .painel-nav {
    position: static;
    height: auto;
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 12px 16px;
    border-right: 0;
    border-bottom: 1px solid var(--panel-border);
  }

  .nav-item {
    width: auto;
    min-width: max-content;
    margin-bottom: 0;
  }

  .painel-body {
    width: 100%;
    padding: 16px;
  }

  .card {
    padding: 16px;
  }

  .card-header,
  .log-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .form-novo {
    grid-template-columns: 1fr;
  }

  .stat-grid {
    grid-template-columns: 1fr 1fr;
  }

  .stat-grid-3 {
    grid-template-columns: 1fr;
  }

  .dash-cols {
    grid-template-columns: 1fr;
  }

  .fallback-grid {
    grid-template-columns: 1fr;
  }

  .chart-bars {
    height: 130px;
    gap: 5px;
  }

  .stat-card {
    min-height: 118px;
    padding: 16px;
  }

  .form-novo-btns,
  .acoes {
    width: 100%;
  }

  .form-novo-btns .btn-sm,
  .acoes .btn-sm {
    flex: 1;
  }

  .tabela {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
}
</style>
